import { RULES_CONTENT, type RulesSection } from '../../data/rulesContent.ts';

export class RulesModal {
  private root: HTMLElement;
  private isOpen = false;
  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.close();
  };

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className =
      'fixed inset-0 z-[70] hidden items-center justify-center bg-wood-coffee/75 backdrop-blur-sm p-4';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', '게임 방법');
    parent.appendChild(this.root);
  }

  mount(): void {
    this.render();
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.close();
    });
  }

  unmount(): void {
    document.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.root.classList.remove('hidden');
    this.root.classList.add('flex');
    document.addEventListener('keydown', this.onKeyDown);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.add('hidden');
    this.root.classList.remove('flex');
    document.removeEventListener('keydown', this.onKeyDown);
  }

  private render(): void {
    this.root.innerHTML = `
      <div class="rounded-2xl bg-wood-paper ring-1 ring-wood-sand max-w-lg w-full max-h-[85dvh] flex flex-col overflow-hidden shadow-xl mlandscape:max-w-[min(720px,calc(100dvw-2rem))] mlandscape:max-h-[calc(100dvh-1.5rem)]">
        <div class="flex items-center justify-between gap-3 px-6 py-4 bg-wood-paper border-b border-wood-sand/60 shrink-0 mlandscape:py-2.5">
          <h2 class="text-2xl font-extrabold tracking-tight text-wood-ink mlandscape:text-xl">게임 방법</h2>
          <button
            type="button"
            data-role="close"
            aria-label="닫기"
            class="rounded-lg p-1 text-wood-coffee/70 hover:text-wood-ink hover:bg-wood-cream ring-1 ring-transparent hover:ring-wood-sand"
          >
            ✕
          </button>
        </div>
        <div class="flex flex-col gap-5 text-wood-coffee px-6 py-5 overflow-y-auto mlandscape:grid mlandscape:grid-cols-2 mlandscape:gap-x-6 mlandscape:gap-y-4 mlandscape:px-5 mlandscape:py-4 mlandscape:text-sm">
          ${RULES_CONTENT.map(renderSection).join('')}
        </div>
      </div>
    `;
    this.root
      .querySelector<HTMLButtonElement>('[data-role="close"]')
      ?.addEventListener('click', () => {
        this.close();
      });
  }
}

function renderSection(section: RulesSection): string {
  const blocks = section.blocks
    .map((block) => {
      if (block.kind === 'p') {
        return `<p class="leading-relaxed text-wood-coffee">${escapeHtml(block.text)}</p>`;
      }
      const items = block.items
        .map((item) => `<li class="leading-relaxed">${escapeHtml(item)}</li>`)
        .join('');
      return `<ul class="list-disc list-outside pl-5 flex flex-col gap-1.5 text-wood-coffee">${items}</ul>`;
    })
    .join('');
  return `
    <section class="flex flex-col gap-2">
      <h3 class="text-lg font-bold text-card-brass">${escapeHtml(section.title)}</h3>
      ${blocks}
    </section>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
